import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Select } from '@ngxs/store';
import { SharedSelectors } from '@app/shared/shared-state';
import { Observable, ReplaySubject } from 'rxjs';
import { HintModel } from '@data-layer/models/common';
import { takeUntil } from 'rxjs/operators';
import { GenericService } from '@services/generic.service';
import { EnumHintKey } from '@data-layer/enums/common';

@Component({
  selector: 'app-hint-tooltip-icon',
  template: `
    <ng-container *ngIf="!isHideCircle">
      <i
        class="fa fa-question-circle d-none d-lg-inline"
        nz-popover
        nzPopoverOverlayClassName="tooltip-sm"
        nzPopoverTrigger="hover"
        [nzPopoverContent]="titleTemplate"
      ></i>

      <i class="fa fa-question-circle d-lg-none" (click)="setModalShown()"></i>
    </ng-container>

    <div
      nz-popover
      nzPopoverOverlayClassName="tooltip-sm"
      nzPopoverTrigger="hover"
      [nzPopoverContent]="titleTemplate"
      *ngIf="isShowCustom"
      (click)="setModalShown()"
    >
      <ng-content select="[hint-container]"></ng-content>
    </div>

    <ng-template #titleTemplate>
      <div class="tooltip-sm-content" [innerHTML]="message || ('PLEASE_CONTACT_SUPPORT_TEAM' | translate)"></div>
    </ng-template>

    <custom-modal [header]="title" [open]="isMobileHintShow" (openChange)="setModalShown()">
      <div [innerHTML]="message || ('PLEASE_CONTACT_SUPPORT_TEAM' | translate)"></div>
    </custom-modal>
  `,
  styleUrls: ['./hint-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HintTooltipComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public title: string = '';
  @Input() public hint: string;
  @Input() public isHideCircle: boolean;
  @Input() public isShowCustom: boolean;
  @Input() public isSkipMobileModal: boolean;

  @Select(SharedSelectors.getAllHints) public hints$: Observable<HintModel[]>;

  public message: string;
  public allMessages: HintModel[] = [];
  public isMobileHintShow: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private genericService: GenericService) {}

  // #13275 — the hint input can change after init (e.g. the partnership-type tooltip follows the
  // GP/LP/LLP selection); without this the tooltip kept the text it was mounted with
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.hint) {
      this.getMessage();
    }
  }

  public ngOnInit(): void {
    this.hints$.pipe(takeUntil(this.destroy$)).subscribe((messages: HintModel[]) => {
      this.allMessages = messages;
      this.getMessage();
    });

    this.genericService.resize$.pipe(takeUntil(this.destroy$)).subscribe(() => (this.isMobileHintShow = false));

    this.genericService.recalcHints$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getMessage();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public setModalShown(): void {
    if (this.isSkipMobileModal) {
      return;
    }

    this.isMobileHintShow = !this.isMobileHintShow;
  }

  private getMessage(): void {
    this.message = this.allMessages.find((item: HintModel) => item.key === this.hint)?.content;

    const hintValues: string[] = Object.values(EnumHintKey);
    if (!this.message && !(hintValues || []).includes(this.hint)) {
      this.message = this.hint;
    }
  }
}
